// src/pages/masterdata/towersite/TowerSiteList.jsx
import React, { useState, useEffect } from 'react';
import { Eye, Edit2, Trash2, Radio, ArrowLeft, Filter, Download } from 'lucide-react';
import toast from 'react-hot-toast';
import DataTable from '../../../components/common/DataTable';
import TowerSiteForm from './TowerSiteForm';
import towerSiteService from '../../../services/towerSiteService';

const TowerSiteList = () => {
  const [view, setView] = useState('list'); // 'list', 'edit', 'detail'
  const [selectedSite, setSelectedSite] = useState(null);
  const [sites, setSites] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Pagination & Sorting State
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const [sortConfig, setSortConfig] = useState({ sortBy: 'siteId', direction: 'ASC' });
  const [searchValue, setSearchValue] = useState('');

  // Filter State
  const [filterRegional, setFilterRegional] = useState('ALL');
  const [filterTypeContract, setFilterTypeContract] = useState('ALL');
  const [showFilters, setShowFilters] = useState(false);

  // Statistics
  const [statistics, setStatistics] = useState(null);

  // Fetch sites on mount and when filters change
  useEffect(() => {
    fetchSites();
    fetchStatistics();
  }, [currentPage, pageSize, sortConfig, searchValue, filterRegional, filterTypeContract]);

  // Fetch all sites
  const fetchSites = async () => {
    setIsLoading(true);
    try {
      const filters = {
        regional: filterRegional !== 'ALL' ? filterRegional : undefined,
        typeSiteContract: filterTypeContract !== 'ALL' ? filterTypeContract : undefined,
        siteName: searchValue || undefined
      };

      const sort = `${sortConfig.sortBy},${sortConfig.direction.toLowerCase()}`;
      const data = await towerSiteService.searchTowerSites(filters, currentPage, pageSize);
      
      setSites(data.content || []);
      setTotalElements(data.totalElements || 0);
    } catch (error) {
      console.error('Error fetching sites:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch statistics
  const fetchStatistics = async () => {
    try {
      const stats = await towerSiteService.getTowerSiteStatistics();
      setStatistics(stats);
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  };

  // Define columns for DataTable
  const columns = [
    {
      key: 'no',
      label: 'No',
      sortable: false,
      width: 'w-16',
      render: (row, index) => (
        <span className="font-medium text-gray-900">
          {currentPage * pageSize + index + 1}
        </span>
      )
    },
    {
      key: 'siteId',
      label: 'Site ID',
      sortable: true,
      render: (row) => (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-800">
          {row.siteId}
        </span>
      )
    },
    {
      key: 'siteName',
      label: 'Site Name',
      sortable: true,
      nowrap: false,
      render: (row) => (
        <div className="max-w-xs">
          <div className="font-medium text-gray-900">{row.siteName}</div>
          {row.projectName && (
            <div className="text-xs text-gray-500 mt-1">{row.projectName}</div>
          )}
        </div>
      )
    },
    {
      key: 'regional',
      label: 'Regional',
      sortable: true,
      render: (row) => (
        <div>
          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
            towerSiteService.getRegionalBadgeClass(row.regional)
          }`}>
            {row.regional}
          </span>
          {row.kabupaten && <div className="text-xs text-gray-500 mt-1">{row.kabupaten}</div>}
        </div>
      )
    },
    {
      key: 'towerHeight',
      label: 'Tower Height',
      sortable: true,
      render: (row) => (
        <div className="text-center">
          <div className="text-sm font-semibold text-gray-900">{row.towerHeight} m</div>
        </div>
      )
    },
    {
      key: 'typeSiteContract',
      label: 'Type',
      sortable: true,
      render: (row) => (
        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
          towerSiteService.getTypeContractBadgeClass(row.typeSiteContract)
        }`}>
          {row.typeSiteContract}
        </span>
      )
    },
    {
      key: 'status',
      label: 'Status',
      sortable: false,
      render: (row) => (
        <div>
          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
            towerSiteService.getStatusBadgeClass(row.status)
          }`}>
            {towerSiteService.getStatusDisplayName(row.status)}
          </span>
          {row.progressPercentage !== null && (
            <div className="mt-1">
              <div className="flex items-center gap-2">
                <div className="flex-1 bg-gray-200 rounded-full h-1.5">
                  <div 
                    className="bg-blue-600 h-1.5 rounded-full transition-all"
                    style={{ width: `${row.progressPercentage}%` }}
                  ></div>
                </div>
                <span className="text-xs font-medium text-gray-600">{row.progressPercentage}%</span>
              </div>
            </div>
          )}
        </div>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      sortable: false,
      render: (row) => (
        <div className="flex gap-2">
          <button
            onClick={() => handleView(row)}
            className="text-blue-600 hover:text-blue-800 p-2 hover:bg-blue-50 rounded-lg transition-colors"
            title="View Details"
          >
            <Eye className="w-5 h-5" />
          </button>
          <button
            onClick={() => handleEdit(row)}
            className="text-green-600 hover:text-green-800 p-2 hover:bg-green-50 rounded-lg transition-colors"
            title="Edit"
          >
            <Edit2 className="w-5 h-5" />
          </button>
          <button
            onClick={() => handleDelete(row)}
            className="text-red-600 hover:text-red-800 p-2 hover:bg-red-50 rounded-lg transition-colors"
            title="Delete"
          >
            <Trash2 className="w-5 h-5" />
          </button>
        </div>
      )
    }
  ];

  // Handlers
  const handleView = async (site) => {
    try {
      const siteDetail = await towerSiteService.getTowerSiteById(site.id);
      setSelectedSite(siteDetail);
      setView('detail');
    } catch (error) {
      console.error('Error fetching site details:', error);
      toast.error('Failed to load site details');
    }
  };

  const handleEdit = async (site) => {
    try {
      const siteDetail = await towerSiteService.getTowerSiteById(site.id);
      setSelectedSite(siteDetail);
      setView('edit');
    } catch (error) {
      console.error('Error fetching site details:', error);
      toast.error('Failed to load site details');
    }
  };

  const handleDelete = async (site) => {
    if (window.confirm(`Are you sure you want to delete tower site ${site.siteId}?`)) {
      try {
        await towerSiteService.softDeleteTowerSite(site.id);
        toast.success('Tower site deleted successfully!');
        fetchSites();
        fetchStatistics();
      } catch (error) {
        console.error('Error deleting site:', error);
      }
    }
  };

  const handleBackToList = () => {
    setView('list');
    setSelectedSite(null);
    fetchSites();
    fetchStatistics();
  };

  const handleSort = (newSortConfig) => {
    setSortConfig(newSortConfig);
    setCurrentPage(0);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(0);
  };

  const handleSearch = (value) => {
    setSearchValue(value);
    setCurrentPage(0);
  };

  const clearFilters = () => {
    setFilterRegional('ALL');
    setFilterTypeContract('ALL');
    setSearchValue('');
    setCurrentPage(0);
  };

  // Render Form View (Edit Only)
  if (view === 'edit') {
    return (
      <TowerSiteForm
        onBack={handleBackToList}
        siteData={selectedSite}
        mode="edit"
      />
    );
  }

  // Render Detail View
  if (view === 'detail' && selectedSite) {
    return (
      <div className="space-y-6">
        {/* Page Header */}
        <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={handleBackToList}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-800">Tower Site Details</h1>
                <p className="text-gray-600 mt-1">View tower site information</p>
              </div>
            </div>
            <button
              onClick={() => handleEdit(selectedSite)}
              className="bg-red-600 hover:bg-red-700 text-white font-medium px-6 py-3 rounded-lg transition-all shadow-md hover:shadow-lg flex items-center gap-2"
            >
              <Edit2 className="w-5 h-5" />
              Edit Site
            </button>
          </div>
        </div>

        {/* Detail Card */}
        <div className="bg-white rounded-lg shadow-md border border-gray-200">
          <div className="p-6">
            <h2 className="text-lg font-bold text-gray-800 mb-4">Basic Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <label className="block text-sm font-semibold text-gray-500 mb-2">Site ID</label>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-purple-100 text-purple-800">
                  {selectedSite.siteId}
                </span>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <label className="block text-sm font-semibold text-gray-500 mb-2">Type Contract</label>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${
                  towerSiteService.getTypeContractBadgeClass(selectedSite.typeSiteContract)
                }`}>
                  {selectedSite.typeSiteContract}
                </span>
              </div>
              <div className="md:col-span-2 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <label className="block text-sm font-semibold text-gray-500 mb-2">Site Name</label>
                <p className="text-lg font-medium text-gray-900">{selectedSite.siteName}</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <label className="block text-sm font-semibold text-gray-500 mb-2">Regional</label>
                <p className="text-base text-gray-900">{selectedSite.regional}</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <label className="block text-sm font-semibold text-gray-500 mb-2">Kabupaten</label>
                <p className="text-base text-gray-900">{selectedSite.kabupaten}</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <label className="block text-sm font-semibold text-gray-500 mb-2">Tower Height</label>
                <p className="text-2xl font-bold text-gray-900">{selectedSite.towerHeight} m</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <label className="block text-sm font-semibold text-gray-500 mb-2">Status</label>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${
                  towerSiteService.getStatusBadgeClass(selectedSite.status)
                }`}>
                  {towerSiteService.getStatusDisplayName(selectedSite.status)}
                </span>
              </div>
            </div>
          </div>

          {/* Location */}
          <div className="border-t border-gray-200 p-6">
            <h2 className="text-lg font-bold text-gray-800 mb-4">Location Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <label className="block text-sm font-semibold text-gray-500 mb-2">Address</label>
                <p className="text-base text-gray-900">{selectedSite.address || '-'}</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <label className="block text-sm font-semibold text-gray-500 mb-2">Coordinates</label>
                <p className="text-base text-gray-900">
                  Lat: {selectedSite.latitude || '-'}, Long: {selectedSite.longitude || '-'}
                </p>
              </div>
            </div>
          </div>

          {/* Project Info */}
          {selectedSite.projectCode && (
            <div className="border-t border-gray-200 p-6">
              <h2 className="text-lg font-bold text-gray-800 mb-4">Project Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <label className="block text-sm font-semibold text-gray-500 mb-2">Project Code</label>
                  <p className="text-base text-gray-900">{selectedSite.projectCode}</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <label className="block text-sm font-semibold text-gray-500 mb-2">SPK Number</label>
                  <p className="text-base text-gray-900">{selectedSite.spkNumber}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Render List View (Default)
  return (
    <div className="space-y-6">
      {/* Statistics Cards */}
      {statistics && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-gradient-to-br from-purple-600 to-purple-700 rounded-lg shadow-lg p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm font-medium">Total Sites</p>
                <p className="text-3xl font-bold mt-2">{statistics.totalSites}</p>
              </div>
              <div className="w-12 h-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                <Radio className="w-6 h-6" />
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-red-600 to-red-500 rounded-lg shadow-lg p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-red-100 text-sm font-medium">Total Height</p>
                <p className="text-3xl font-bold mt-2">{statistics.totalTowerHeight?.toLocaleString()} m</p>
              </div>
              <div className="w-12 h-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                <Radio className="w-6 h-6" />
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-green-600 to-green-500 rounded-lg shadow-lg p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm font-medium">Avg Height</p>
                <p className="text-3xl font-bold mt-2">{statistics.averageTowerHeight?.toFixed(0)} m</p>
              </div>
              <div className="w-12 h-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                <Radio className="w-6 h-6" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Page Header with Filters */}
      <div className="bg-white rounded-lg shadow-md border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-600 to-purple-700 rounded-lg flex items-center justify-center shadow-md">
                <Radio className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800">Tower Site List</h1>
                <p className="text-gray-600 mt-1">Manage tower infrastructure sites</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="border-2 border-blue-600 text-blue-600 hover:bg-blue-50 font-medium px-4 py-2 rounded-lg transition-colors flex items-center gap-2"
              >
                <Filter className="w-4 h-4" />
                Filters
              </button>
              <button
                className="border border-gray-300 text-gray-700 hover:bg-gray-50 font-medium px-4 py-2 rounded-lg transition-colors flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Export
              </button>
            </div>
          </div>

          {/* Filter Section */}
          {showFilters && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Regional</label>
                  <select
                    value={filterRegional}
                    onChange={(e) => {
                      setFilterRegional(e.target.value);
                      setCurrentPage(0);
                    }}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                  >
                    <option value="ALL">All Regionals</option>
                    <option value="JAWA TIMUR">JAWA TIMUR</option>
                    <option value="JAWA TENGAH">JAWA TENGAH</option>
                    <option value="JAWA BARAT">JAWA BARAT</option>
                    <option value="JABODETABEK">JABODETABEK</option>
                    <option value="SUMATRA">SUMATRA</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Type Contract</label>
                  <select
                    value={filterTypeContract}
                    onChange={(e) => {
                      setFilterTypeContract(e.target.value);
                      setCurrentPage(0);
                    }}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                  >
                    <option value="ALL">All Types</option>
                    <option value="MACRO">MACRO</option>
                    <option value="Macro">Macro</option>
                    <option value="MICRO">MICRO</option>
                  </select>
                </div>
                <div className="flex items-end">
                  <button
                    onClick={clearFilters}
                    className="w-full border border-gray-300 text-gray-700 hover:bg-gray-100 font-medium px-4 py-2 rounded-lg transition-colors"
                  >
                    Clear Filters
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* DataTable Component */}
        <DataTable
          columns={columns}
          data={sites}
          currentPage={currentPage}
          pageSize={pageSize}
          totalElements={totalElements}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          onSort={handleSort}
          sortConfig={sortConfig}
          isLoading={isLoading}
          onSearch={handleSearch}
          searchValue={searchValue}
          searchPlaceholder="Search by site ID, site name..."
          enableQuickJump={true}
          customPageSizes={[10, 25, 50, 100]}
          striped={false}
          headerBgColor="bg-gradient-to-r from-red-600 to-red-500"
          headerTextColor="text-white"
          hoverColor="hover:bg-red-50"
        />
      </div>
    </div>
  );
};

export default TowerSiteList;